import { type ApiContact, type InviteToPartnershipRequest } from '@Model';
import { Button, iconSizeSm, isNullOrEmpty, useToasts, type Result } from '@synqo/frontend-component-library';
import { IconCheck, IconClock, IconLoader2, IconUserPlus } from '@tabler/icons-react';
import { useCallback, useState, type MouseEventHandler } from 'react';
import styles from './InviteToPartnershipButton.module.css';

interface InviteToPartnershipProperties {
    contact: ApiContact;
    addressId: string;
    inviteToPartnership: (request: InviteToPartnershipRequest) => Promise<Result<boolean>>;
    locked?: boolean;
    alreadyHasAccess?: boolean;
    /** The person was invited but has not accepted the partnership yet, so they cannot log in. */
    invitationPending?: boolean;
    /** The person refused the invitation. Inviting them again starts the decision over. */
    invitationRejected?: boolean;
}

export function InviteToPortalButton({
    contact,
    addressId,
    locked,
    inviteToPartnership,
    alreadyHasAccess,
    invitationPending,
    invitationRejected,
}: InviteToPartnershipProperties) {
    const { pushAllToasts } = useToasts();
    const [isInviting, setIsInviting] = useState(false);

    const hasEmail = !isNullOrEmpty(contact.email);
    const canInvite = !locked && hasEmail && (invitationRejected || (!alreadyHasAccess && !invitationPending));

    const inviteCallback = useCallback(
        (contact: ApiContact) => {
            return inviteToPartnership({
                email: contact.email,
                first_name: contact.first_name,
                last_name: contact.last_name,
                customer_address_id: addressId,
            });
        },
        [inviteToPartnership, addressId],
    );

    const handleInvite = useCallback(async () => {
        if (!canInvite) {
            return;
        }
        setIsInviting(true);
        try {
            const result = await inviteCallback(contact);
            pushAllToasts(result.messages);
        } catch (error) {
            console.error('Error inviting to partnership:', error);
        } finally {
            setIsInviting(false);
        }
    }, [canInvite, contact, setIsInviting, inviteCallback, pushAllToasts]);

    const onClick = useCallback<MouseEventHandler>(
        (event) => {
            event.stopPropagation();
            handleInvite();
        },
        [handleInvite],
    );

    if (invitationPending) {
        return (
            <Button
                onClick={onClick}
                variant="secondary"
                locked
                tooltip="Diese Person wurde eingeladen, hat den Zugang aber noch nicht bestätigt."
            >
                <IconClock size={iconSizeSm} />
                &nbsp;Einladung offen
            </Button>
        );
    } else if (invitationRejected && hasEmail && !locked) {
        return (
            <Button
                onClick={onClick}
                variant="secondary"
                tooltip="Diese Person hat die Einladung abgelehnt. Erneut einladen?"
            >
                <IconUserPlus size={iconSizeSm} />
                &nbsp;Erneut einladen
            </Button>
        );
    } else if (alreadyHasAccess) {
        return (
            <Button
                onClick={onClick}
                variant="success"
                locked
                tooltip="Diese Person hat bereits Zugriff auf das Portal."
            >
                <IconCheck size={iconSizeSm} />
                &nbsp;Bereits freigeschaltet
            </Button>
        );
    } else if (!hasEmail) {
        return (
            <Button onClick={onClick} locked tooltip="Nur Personen mit gültiger Email können eingeladen werden.">
                <IconUserPlus size={iconSizeSm} />
                &nbsp;Person hat keine Email
            </Button>
        );
    } else if (locked) {
        return (
            <Button onClick={onClick} locked tooltip="Speichern sie die Adresse, um einen Portal-Nutzer einzuladen.">
                <IconUserPlus size={iconSizeSm} />
                &nbsp;Beim Bearbeiten deaktiviert
            </Button>
        );
    } else {
        return (
            <Button onClick={onClick}>
                {isInviting ? (
                    <IconLoader2 size={iconSizeSm} className={styles.spinner} />
                ) : (
                    <IconUserPlus size={iconSizeSm} />
                )}
                &nbsp;Portal-Nutzer einladen
            </Button>
        );
    }
}
